'use client';

import {useEffect, useRef} from 'react';
import './AlertInvestigation.scss';

const Frame = ({path, pill, pillColor, caption, children}) => <figure className='ai-fig'>
  <div className='ai-fig-frame'>
    <div className='ai-fig-chrome'>
      <span className='ai-fig-dot'/>
      <span className='ai-fig-path'>{path}</span>
      <span className={`ai-fig-pill ${pillColor}`}>{pill}</span>
    </div>
    {children}
  </div>
  <figcaption>{caption}</figcaption>
</figure>;

const Fields = ({rows}) => <div className='ai-fig-kv'>
  {rows.map(([k, v]) => <div className='ai-fig-kv-row' key={k}>
    <div className='ai-fig-kv-key'>{k}</div>
    <div className='ai-fig-kv-val'>{v}</div>
  </div>)}
</div>;

export const AlertCard = () => <Frame
  path='/alerts/a-77213' pill='New' pillColor='amber'
  caption={<><b>Figure 1.</b> The alert as it arrives, showing everything the detection recorded about the event.</>}>
  <div className='ai-fig-card'>
    <div className='ai-fig-title'>Inbox rule created with hide-and-mark-read behavior</div>
    <div className='ai-fig-sub'>Microsoft 365 integration · severity medium</div>
    <Fields rows={[
      ['Mailbox', <code key='m'>j.o••••@•••••.com</code>],
      ['Rule name', '. (single period)'],
      ['Action', 'Move to RSS Feeds, mark as read'],
      ['Created', '03:14:22 UTC'],
      ['Source IP', <code key='ip'>45.147.x.x</code>]
    ]}/>
  </div>
</Frame>;

const STEPS = [
  ['02:41', 'EMAIL', '', <>A document-signing notification arrives for seven recipients in finance and operations, sent from a lookalike domain registered eleven days earlier.</>, 'Passed authentication checks. The domain had no prior reputation either way.'],
  ['02:53', 'EMAIL', '', <>The user follows the link. One other recipient clicks four minutes later.</>, 'The second click is not in the alert, and it is a second account to check.'],
  ['02:58', 'IDENTITY', 'warn', <>The user completes a multi-factor prompt on the attacker&apos;s proxy page.</>, 'The credentials and the session token are both captured at this point.'],
  ['03:07', 'IDENTITY', 'bad', <>Sign-in succeeds from <code>45.147.x.x</code> with no multi-factor prompt, because the stolen session was replayed rather than reused as a password.</>, 'This detail determines the remediation.'],
  ['03:14', 'CLOUD APPS', 'bad', <>The inbox rule is created. <b>This is the alert.</b></>, 'Thirty-three minutes after the first email and six steps into the intrusion.'],
  ['03:22', 'CLOUD APPS', 'bad', <>Sixty-one messages are opened, concentrated on threads containing invoices and payment details.</>, null],
  ['04:02', 'EMAIL', 'bad', <>Three messages go out to external finance contacts asking them to update remittance details before the next payment run.</>, 'The replies were being routed to RSS Feeds by the rule, which is what the rule was for.'],
  ['05:47', 'NETWORK', 'warn', <>A VPN authentication attempt for the same account fails against a certificate requirement.</>, 'They tried to widen access and could not.'],
  ['06:15', 'IDENTITY', 'bad', <>The session is used again, and is still valid at the moment the investigation runs.</>, 'Still active when the investigation ran.']
];

export const Timeline = () => <Frame
  path='/alerts/a-77213/timeline' pill='Reconstructed by Cyra' pillColor='blue'
  caption={<><b>Figure 3.</b> The reconstruction. Nine of the seventeen events are shown; the rest establish scope, including the other five recipients and the endpoint checks that came back clean.</>}>
  <div className='ai-fig-tl'>
    {STEPS.map(([time, src, level, ev, note]) => <div className={`ai-fig-step ${level}`} key={time}>
      <div className='ai-fig-time'>{time}</div>
      <div className='ai-fig-rail'><span className='ai-fig-node'/></div>
      <div className='ai-fig-bd'>
        <div className='ai-fig-src'>{src}</div>
        <div className='ai-fig-ev'>{ev}</div>
        {note && <div className='ai-fig-note'>{note}</div>}
      </div>
    </div>)}
  </div>
</Frame>;

export const TicketCard = () => <Frame
  path='/tickets/t-40922' pill='Escalated' pillColor='red'
  caption={<><b>Figure 4.</b> What actually lands in the queue. The original alert triggered the investigation; nothing in it required a person to read it until Cyra decided the activity was worth escalating and attached its own findings.</>}>
  <div className='ai-fig-card'>
    <div className='ai-fig-title'>Credential compromise, active session, ongoing financial fraud</div>
    <div className='ai-fig-sub'>Raised by Cyra · investigated in 41 seconds</div>
    <Fields rows={[
      ['Summary', 'Phishing led to a captured session token. The account signed in without multi-factor, created a mail-hiding rule, read financial threads, and sent three messages requesting changed payment details. The session is still active.'],
      ['Root cause', 'Session token theft via an adversary-in-the-middle phishing page'],
      ['Scope', 'One confirmed account, one additional recipient to verify'],
      ['Recommended action', 'Revoke sessions, reset password, recall 3 messages, check second recipient. Endpoint and network checked, no action needed there.'],
      ['Evidence', '17 supporting events across email, identity, and cloud app logs, attached below']
    ]}/>
  </div>
</Frame>;

// Animated view of every event in the window, narrowing to the seventeen that matter.
const drawHaystack = cv => {
  const x = cv.getContext('2d');
  const W = 900, H = 430;
  const LANES = ['Email', 'Identity', 'Endpoint', 'Network', 'Cloud apps'];
  const LEFT = 96, RIGHT = 26, TOP = 52, BOT = 58;
  const PW = W - LEFT - RIGHT, PH = H - TOP - BOT, LH = PH / LANES.length;
  const TOTAL = 412847;

  const NEEDLES = [
    [0, 0.383], [0, 0.391], [0, 0.402], [0, 0.417], [1, 0.425], [1, 0.431], [4, 0.438],
    [0, 0.446], [1, 0.454], [4, 0.461], [0, 0.470], [0, 0.474], [3, 0.483], [1, 0.491],
    [2, 0.498], [1, 0.506], [4, 0.512]];
  const ALERT_I = 6;

  // deterministic haystack
  let seed = 99;
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) & 0x7fffffff;
    return seed / 0x7fffffff;
  };
  const dots = [];
  const DENSITY = [0.30, 0.24, 0.22, 0.14, 0.10];
  for (let l = 0; l < 5; l++) {
    const n = Math.round(2600 * DENSITY[l] * 5);
    for (let i = 0; i < n; i++) {
      const t = rnd();
      // diurnal shaping so it looks like real ingest
      const w = 0.35 + 0.65 * Math.pow(Math.sin(Math.PI * Math.min(1, Math.max(0, t * 1.05))) + 0.12, 2);
      if (rnd() > w) {
        continue;
      }
      dots.push({l, t, y: rnd(), a: 0.10 + rnd() * 0.26});
    }
  }

  const PHASE = [2200, 1500, 1800, 3200]; // fill, scan, lock, hold
  const CYCLE = PHASE.reduce((a, b) => a + b, 0) + 900;
  const phaseAt = ms => {
    const a = PHASE[0], b = a + PHASE[1], c = b + PHASE[2], d = c + PHASE[3];
    if (ms < a) return [0, ms / a];
    if (ms < b) return [1, (ms - a) / PHASE[1]];
    if (ms < c) return [2, (ms - b) / PHASE[2]];
    if (ms < d) return [3, (ms - c) / PHASE[3]];
    return [4, (ms - d) / 900];
  };
  const px = t => LEFT + t * PW;
  const py = (l, f) => TOP + l * LH + 6 + f * (LH - 12);

  const draw = ms => {
    const [p, f] = phaseAt(ms);
    x.fillStyle = '#12151a';
    x.fillRect(0, 0, W, H);

    // lane bands + labels
    for (let l = 0; l < 5; l++) {
      if (l % 2 === 0) {
        x.fillStyle = '#151920';
        x.fillRect(LEFT, TOP + l * LH, PW, LH);
      }
      x.fillStyle = '#6f7a89';
      x.font = '11px system-ui,-apple-system,Segoe UI,sans-serif';
      x.textAlign = 'right';
      x.fillText(LANES[l], LEFT - 14, TOP + l * LH + LH / 2 + 4);
      x.fillStyle = '#1d222b';
      x.fillRect(LEFT, TOP + l * LH, PW, 1);
    }
    x.fillStyle = '#1d222b';
    x.fillRect(LEFT, TOP + PH, PW, 1);

    // haystack
    const shown = p === 0 ? f : 1;
    let dim = p >= 2 ? Math.max(0.16, 1 - f * 2.2) : 1;
    if ((p === 2 && f > 0.45) || p >= 3) {
      dim = 0.16;
    }
    for (const d of dots) {
      if (d.t > shown) {
        continue;
      }
      x.fillStyle = `rgba(150,166,188,${d.a * dim})`;
      x.fillRect(px(d.t), py(d.l, d.y), 1.5, 1.5);
    }

    // scan sweep
    if (p === 1) {
      const sx = LEFT + f * PW;
      const g = x.createLinearGradient(sx - 70, 0, sx, 0);
      g.addColorStop(0, 'rgba(77,143,240,0)');
      g.addColorStop(1, 'rgba(77,143,240,.14)');
      x.fillStyle = g;
      x.fillRect(sx - 70, TOP, 70, PH);
      x.fillStyle = 'rgba(77,143,240,.75)';
      x.fillRect(sx, TOP, 1, PH);
    }

    // needle band + needles
    if (p >= 2) {
      const lo = px(0.375), hi = px(0.520);
      const bandA = p === 2 ? Math.min(1, f * 2.6) : 1;
      x.fillStyle = `rgba(77,143,240,${0.06 * bandA})`;
      x.fillRect(lo, TOP, hi - lo, PH);
      x.fillStyle = `rgba(77,143,240,${0.35 * bandA})`;
      x.fillRect(lo, TOP, 1, PH);
      x.fillRect(hi, TOP, 1, PH);

      const reveal = p === 2 ? Math.max(0, (f - 0.25) / 0.75) : 1;
      const count = Math.round(reveal * NEEDLES.length);
      // connecting thread
      if (count > 1) {
        x.beginPath();
        for (let k = 0; k < count; k++) {
          const nx = px(NEEDLES[k][1]), ny = py(NEEDLES[k][0], 0.5);
          k ? x.lineTo(nx, ny) : x.moveTo(nx, ny);
        }
        x.strokeStyle = 'rgba(77,143,240,.30)';
        x.lineWidth = 1;
        x.stroke();
      }
      for (let j = 0; j < count; j++) {
        const ax = px(NEEDLES[j][1]), ay = py(NEEDLES[j][0], 0.5);
        const isAlert = j === ALERT_I;
        const pulse = 1 + 0.35 * Math.sin(ms / 260 + j);
        x.fillStyle = isAlert ? 'rgba(217,164,65,.20)' : 'rgba(77,143,240,.18)';
        x.beginPath();
        x.arc(ax, ay, 6.5 * pulse, 0, 7);
        x.fill();
        x.fillStyle = isAlert ? '#d9a441' : '#4d8ff0';
        x.beginPath();
        x.arc(ax, ay, 2.6, 0, 7);
        x.fill();
      }
      if (p >= 3) {
        const axx = px(NEEDLES[ALERT_I][1]), ayy = py(NEEDLES[ALERT_I][0], 0.5);
        x.strokeStyle = 'rgba(217,164,65,.5)';
        x.lineWidth = 1;
        x.beginPath();
        x.moveTo(axx, ayy - 11);
        x.lineTo(axx, TOP - 16);
        x.stroke();
        x.fillStyle = '#d9a441';
        x.textAlign = 'center';
        x.font = '10px ui-monospace,Menlo,monospace';
        x.fillText('THE ALERT', axx, TOP - 21);
      }
    }

    // header counters
    x.textAlign = 'left';
    x.font = '10px ui-monospace,SFMono-Regular,Menlo,monospace';
    x.fillStyle = '#6f7a89';
    x.fillText('EVENTS INGESTED', LEFT, 22);
    x.font = '600 19px ui-monospace,SFMono-Regular,Menlo,monospace';
    x.fillStyle = '#e7ecf3';
    const shownN = p === 0 ? Math.round(TOTAL * f) : TOTAL;
    x.fillText(shownN.toLocaleString('en-US'), LEFT, 42);

    if (p >= 2) {
      const alpha = p === 2 ? Math.min(1, f * 2) : 1;
      x.font = '10px ui-monospace,SFMono-Regular,Menlo,monospace';
      x.fillStyle = `rgba(111,122,137,${alpha})`;
      x.fillText('RELEVANT TO THIS ALERT', LEFT + 250, 22);
      x.font = '600 19px ui-monospace,SFMono-Regular,Menlo,monospace';
      x.fillStyle = `rgba(77,143,240,${alpha})`;
      const c2 = p === 2 ? Math.round(Math.max(0, (f - 0.25) / 0.75) * 17) : 17;
      x.fillText(String(c2), LEFT + 250, 42);
    }
    if (p >= 3) {
      x.font = '11px system-ui,-apple-system,Segoe UI,sans-serif';
      x.fillStyle = '#6f7a89';
      x.textAlign = 'right';
      x.fillText('one in every 24,285 events', W - RIGHT, 42);
    }

    // time axis
    x.textAlign = 'center';
    x.font = '10px ui-monospace,SFMono-Regular,Menlo,monospace';
    x.fillStyle = '#4a5462';
    const labels = ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00', '24:00'];
    labels.forEach((label, q) => x.fillText(label, LEFT + (q / (labels.length - 1)) * PW, H - 30));
    x.textAlign = 'left';
    x.fillStyle = '#6f7a89';
    x.font = '11px system-ui,-apple-system,Segoe UI,sans-serif';
    x.fillText(p >= 2 ? '24 hours around the alert' : 'ingesting', LEFT, H - 11);
  };

  return {draw, CYCLE, HOLD: PHASE[0] + PHASE[1] + PHASE[2] + 500};
};

export const Haystack = () => {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    const {draw, CYCLE, HOLD} = drawHaystack(cv);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      draw(HOLD);
      return;
    }

    // only animate while on screen (the feed renders the whole post too)
    let frame = null, t0 = null, visible = false;
    const loop = ts => {
      if (t0 === null) {
        t0 = ts;
      }
      draw((ts - t0) % CYCLE);
      frame = visible ? requestAnimationFrame(loop) : null;
    };
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible && frame === null) {
        frame = requestAnimationFrame(loop);
      }
    });
    draw(0);
    io.observe(cv);

    return () => {
      io.disconnect();
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
    };
  }, []);

  return <Frame
    path='/alerts/a-77213/search' pill='24h window' pillColor='blue'
    caption={<><b>Figure 2.</b> Every event the environment generated in the window around the alert, and the seventeen Cyra pulled out of it. The relevant events sit across five sources and inside about three hours.</>}>
    <div className='ai-fig-viz'>
      <canvas ref={ref} width={900} height={430} aria-label='Animated chart: 412,847 events across five sources narrowing to 17 relevant events within about three hours'/>
    </div>
  </Frame>;
};
